import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/lib/supabase";
import { captureClientEnvironment } from "@/lib/clientEnvironment";

/** Same key src/lib/i18n/index.tsx owns — read directly rather than pulling in the i18n context, so this stays callable from a plain top-level effect. */
const LANGUAGE_STORAGE_KEY = "snapdraw.language";

/** Synchronous, in-memory guard against duplicate concurrent calls within one page load — sessionStorage's flag only reflects success, so it can't alone prevent two inserts firing before the first one's response lands (App.tsx's effect can re-fire on auth-state changes). */
let insertInFlight = false;

/**
 * Fire-and-forget, once per browser tab per signed-in user — not once
 * globally per tab, since a shared/kiosk machine where a different user
 * signs in on the same tab must still get its own row.
 */
export function recordSessionStart(userId: string): void {
  const flagKey = `snapdraw.usageRecorded.${userId}`;
  if (sessionStorage.getItem(flagKey) || insertInFlight) return;
  insertInFlight = true;

  (async () => {
    const env = captureClientEnvironment();
    // Which native build a session came from (backlog MOBILE-16 follow-up)
    // — null on web, and best-effort on native (App.getInfo() failing
    // shouldn't block the rest of this row from being recorded).
    let appVersion: string | null = null;
    if (Capacitor.isNativePlatform()) {
      try {
        appVersion = (await App.getInfo()).version;
      } catch {
        // Leave null — same fail-open reasoning as everywhere else in this
        // file: a usage-tracking nicety should never block on this.
      }
    }
    const { error } = await supabase.from("usage_events").insert({
      user_id: userId,
      route: window.location.pathname,
      user_agent: env.userAgent,
      screen_width: env.screenWidth,
      screen_height: env.screenHeight,
      device_pixel_ratio: env.devicePixelRatio,
      app_language: localStorage.getItem(LANGUAGE_STORAGE_KEY) === "es" ? "es" : "en",
      timezone: env.timezone,
      // 'ios' | 'android' | 'web' (backlog MOBILE-1) — lets admin-stats tell
      // a real native-app session apart from browser traffic, which the UA
      // string alone can't (Capacitor's WebView still reports Safari/Chrome).
      platform: Capacitor.getPlatform(),
      app_version: appVersion,
    });
    insertInFlight = false;
    // Only suppress future attempts this tab session on success — a
    // transient/setup failure (e.g. the migration not applied yet) should
    // retry on the next reload, not be silently blocked for the rest of
    // the session.
    if (error) console.error("recordSessionStart failed (non-blocking):", error);
    else sessionStorage.setItem(flagKey, "1");
  })();
}
